import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import {
	selectCurrentCompany,
	selectCurrentCompanyId,
	selectCurrentProfileType,
	selectCurrentUserId,
	selectUser,
} from "newui/features/Auth/authSelectors";
import { setUserRoles } from "newui/features/Auth/authSlice";
import type { User } from "newui/features/Auth/types/User.type";
import type { UserCompanyProfile } from "newui/features/Auth/types/UserCompanyProfile.type";
import type { UserRoleName } from "newui/features/Auth/types/UserRole.type";
import type { RootState } from "newui/store";
import { useAppSelector } from "newui/store/hooks";
import { useMemo } from "react";
import { CompanyProfileType } from "newui/features/Auth/types/CompanyProfileType";

interface CurrentUser {
	currentUser: User | null;
	guestUserEmail: string | null;
	isCompanyAdmin: boolean;
	isExecutiveUser: boolean;
	isGuest: boolean;
	isSuperAdmin: boolean;
}

export const useCurrentCompany = (): UserCompanyProfile | null => {
	const currentCompany = useAppSelector(selectCurrentCompany);
	return currentCompany as UserCompanyProfile | null;
};

export const useCurrentCompanyId = (): number | null => {
	const currentCompanyId = useAppSelector(selectCurrentCompanyId);
	return currentCompanyId;
};

export const useCurrentProfileType = ():
	CompanyProfileType => {
	return useAppSelector(selectCurrentProfileType);
};

/**
 * Allows you to get any specific value from the current company profile
 * Will ensure only re-renders when that value changes
 * @param key the key from UserCompanyProfile to get
 * @returns key value type
 */
export const useCurrentCompanyValue = <K extends keyof UserCompanyProfile>(
	key: K
): UserCompanyProfile[K] | null => {
	return useAppSelector(
		(state: RootState) => state.auth.currentCompany?.[key] ?? null
	);
};

export const useCurrentUser = (): CurrentUser | null => {
	const currentUser = useAppSelector(selectUser);
	return useMemo(() => {
		if (!currentUser) {
			return {
				currentUser: null,
				guestUserEmail: null,
				isGuest: false,
				isCompanyAdmin: false,
				isSuperAdmin: false,
				isExecutiveUser: false,
			} as CurrentUser | null;
		}

		return {
			currentUser,
			guestUserEmail: currentUser.is_guest ? currentUser.email : '',
			isGuest: currentUser.is_guest,
			isCompanyAdmin: currentUser.company_admin,
			isSuperAdmin: currentUser.is_admin,
			isExecutiveUser: currentUser.is_executive,
		};
	}, [currentUser]) as CurrentUser | null;
};

export const useIsCurrentUserAdmin = (): boolean => {
	const { isCompanyAdmin } = useCurrentUser() || {};
	return !!isCompanyAdmin;
}

export const useCurrentUserId = (): number | null => {
	const currentUserId = useAppSelector(selectCurrentUserId);
	return currentUserId;
};

export const useCurrentUserHasRole = (roleName: UserRoleName): boolean => {
	return useCurrentUserRoles().some((role) => role === roleName);
};

export const useCurrentUserRoles = (): UserRoleName[] => {
	const existingRoles = useSelector(
		(state: RootState) => state?.auth?.user_roles
	);
	const { currentUser } = useCurrentUser();
	const dispatch = useDispatch();

	if (!existingRoles || existingRoles.length === 0) {
		if (!currentUser || !currentUser.roles) {
			return [];
		}
		const userRoles = currentUser.roles.map(
			(role) => role.name as UserRoleName
		);

		dispatch(setUserRoles(userRoles));
	}

	return existingRoles || [];
};
